import { useEffect, useRef, useReducer } from 'react';
import axios from 'axios';
import 'react-toastify/dist/ReactToastify.css';
import { getReducer } from '../../utils';

const initialState = {
  filtersSearch: [],
  filtersRegister: [],
  filtersFamily: [],
  loading: true,
  error: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        filtersSearch: action.payload.full,
        filtersRegister: action.payload.register,
        filtersFamily: action.payload.family,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const staticFilters = [
  {
    id: 1,
    placeholder: 'Lọc theo lớp học',
    image: 'classroom.png',
    keyword: 'class_name',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-chalkboard-user',
    required: false,
  },
  {
    id: 2,
    placeholder: 'Lọc theo ngành học',
    image: 'education.png',
    keyword: 'major',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-graduation-cap',
    required: false,
  },
  {
    id: 3,
    placeholder: 'Lọc theo khoa, trường (đơn vị)',
    image: 'school.png',
    keyword: 'faculty',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-school',
    required: false,
  },
  {
    id: 4,
    placeholder: 'Lọc theo nơi làm việc',
    image: 'briefcase.png',
    keyword: 'company',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-briefcase',
    required: false,
  },
  {
    id: 5,
    placeholder: 'Lọc theo chức vụ',
    image: 'team.png',
    keyword: 'position',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-users',
    required: false,
  },
  {
    id: 6,
    placeholder: 'Lọc theo lĩnh vực hoạt động',
    image: 'activity.png',
    keyword: 'business_field',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-clipboard-list',
    required: false,
  },
  {
    id: 7,
    placeholder: 'Lọc theo địa điểm',
    image: 'location.png',
    keyword: 'work_address',
    isOneSelect: true,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-map-location-dot',
    required: false,
  },
  {
    id: 12,
    placeholder: 'Lọc theo vị trí trong hội',
    image: 'branding.png',
    keyword: 'club_position',
    isOneSelect: false,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-id-badge',
    required: false,
  },
  {
    id: 0,
    placeholder: 'Lọc theo địa chỉ',
    image: 'home-address.png',
    keyword: 'address',
    isOneSelect: false,
    fieldValues: [],
    collum: 6,
    type: 'text',
  },
  {
    id: 100,
    placeholder: 'Lọc theo giới tính',
    image: 'feedback.png',
    fieldValues: [
      { id: 'male', customFieldId: 2, value: 'Nam' },
      { id: 'female', customFieldId: 2, value: 'Nữ' },
      { id: 'other', customFieldId: 2, value: 'Khác' },
    ],
    keyword: 'gender',
    isOneSelect: true,
    collum: 6,
    type: 'text',
  },
];

const staticFamily = [
  {
    id: 8,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-children',
    required: false,
  },
  {
    id: 9,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-briefcase',
    required: false,
  },
  {
    id: 10,
    collum: 6,
    type: 'text',
    icon: 'fa-solid fa-baby',
    required: false,
  },
  {
    id: 11,
    type: 'text',
    icon: 'fa-solid fa-school',
    required: false,
  },
];

const useInfoFilters = () => {
  const [state, dispatch] = useReducer(getReducer(reducer), initialState);
  const hasFetchedRef = useRef(false);

  const fetchProvinces = async () => {
    try {
      const res = await fetch('https://provinces.open-api.vn/api/');
      const data = await res.json();
      return data
        .map((p) => ({
          id: p.code,
          customFieldId: 2,
          value: p.name.replace(/^Tỉnh\s+|^Thành phố\s+/i, ''),
        }))
        .sort((a, b) => a.value.localeCompare(b.value, 'vi'));
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetchedRef.current) return;
      hasFetchedRef.current = true;

      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const [provinces, attrRes, valuesRes] = await Promise.all([
          fetchProvinces(),
          axios.get('/user-attribute/public/shows'),
          axios.get('/field/get-field-values'),
        ]);

        const merged = attrRes.data.data.map((attr) => {
          const matched = valuesRes.data.data.find((f) => f.code === attr.code);
          return {
            ...attr,
            fieldValues: matched?.fieldValues || [],
            text: matched?.text || '',
          };
        });

        const full = staticFilters.map((f) => {
          const dynamic = merged.find((m) => m.id === f.id);
          const isProvince = f.keyword === 'work_address';
          return {
            ...f,
            ...(dynamic || {}),
            fieldValues: isProvince
              ? provinces
              : dynamic?.fieldValues || f.fieldValues || [],
          };
        });

        const register = full.slice(0, 8);

        const family = staticFamily.map((item) => {
          const dynamic = merged.find((m) => m.id === item.id);
          return {
            ...item,
            ...(dynamic || {}),
            fieldValues: dynamic?.fieldValues || item.fieldValues || [],
          };
        });

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { full, register, family },
        });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: err.message });
      }
    };

    fetchData();
  }, []);

  return state;
};

export default useInfoFilters;
